import sharedSupabase from '../config/supabase';
import { buildAllocationLine, computeNeed, recalculateLine } from '../utils/allocationMath';
import { hasPurchasedAlternative, isPreparedRow } from '../utils/preparedReplenishment';
import { resolveRowPar, PAR_STATUS } from '../utils/countUnitModel';
import { planCountGroupFold, restrictGroupsToItems } from '../utils/countGroupFold';

/**
 * Purchase Allocation Service (web)
 *
 * Builds and confirms purchase_allocations from count sessions: aggregate the
 * count into per-item needs, price each need against the item's purchase
 * options, then let the confirm_purchase_allocation RPC fan the plan out to
 * draft purchase orders (delivery vendors) and shopping lists (pickup).
 *
 * Every function takes an optional trailing `client` (defaults to the shared
 * supabase singleton) so tests can inject a fake.
 */

const throwIfError = (error) => {
  if (error) throw new Error(error.message);
};

/**
 * `purchase_allocation_lines` has no column for these — they exist on the line
 * object so the review screen can label a bare "6" as "6 cases at $4.75 each"
 * without re-looking-up the option, and the stored row reaches them through
 * its `option:purchase_options!<selected-option fk>(*)` join instead.
 *
 * Dropping them here rather than not producing them keeps one line shape for
 * both the screen and the table; sending them would fail the insert on a
 * column that does not exist.
 */
const DISPLAY_ONLY_LINE_FIELDS = [
  'description', 'purchase_unit', 'quantity_per_package', 'package_price',
  // Relationship-comparison facts: shown on the review screen, frozen into
  // option_snapshot by the confirm RPC — never line columns of their own.
  'comparison_unit', 'comparison_quantity_per_package', 'comparable_unit_cost',
  'comparison_source', 'comparison_confidence', 'comparison_estimated',
  // The layered order decision (raw need → item target → driver): shown on
  // the worksheet, frozen into option_snapshot by the confirm trigger —
  // derived facts, never line columns of their own.
  'raw_need_quantity', 'purchase_target_quantity', 'target_driver', 'order_driver',
  // ORDER is not BILLING: whether a line's total is settled on a scale, and
  // the rate it settles at ("$16.40/LB × ~60 LB"). Derived from the option's
  // billing basis on every read — never a line column.
  'cost_is_estimated', 'billing_basis_text',
];

const forStorage = (line) => {
  const row = { ...line };
  DISPLAY_ONLY_LINE_FIELDS.forEach((field) => { delete row[field]; });
  return row;
};

// A scenario may move selected_purchase_option_id repeatedly. The original
// planner answer is written once beside it so reset/comparison still has a
// durable baseline after a reload; updateLineOption deliberately never writes
// either recommended_* column.
const forStorageWithRecommendedBaseline = (line) => {
  const row = forStorage(line);
  return {
    ...row,
    recommended_purchase_option_id: row.selected_purchase_option_id ?? null,
    recommended_selection_reason: row.selection_reason ?? null,
  };
};

// These columns have migrations, but stores behind must keep allocating —
// retry once without the provenance/baseline fields on a missing-column error,
// the same degradation contract the option services use.
const isMissingColumnError = (error) =>
  error && (error.code === '42703' || error.code === 'PGRST204'
    || /selection_reason|recommended_purchase_option_id|recommended_selection_reason/.test(String(error.message || '')));

const toFiniteNumber = (value) => {
  if (value === null || value === undefined || value === '') return null;
  const parsed = Number.parseFloat(value);
  return Number.isFinite(parsed) ? parsed : null;
};

// Same fallback id resolution as sharedOrderMath (inventory_item_id first,
// then the snapshot id, then the row id itself).
const resolveInvId = (ci) => ci?.inventory_item_id ?? ci?.item_snapshot?.id ?? ci?.id ?? null;

const resolveDescription = (ci) =>
  ci?.description ?? ci?.item_snapshot?.description ?? ci?.item_snapshot?.name ?? null;

// The unit the line's NEED is denominated in — and therefore the unit stored
// on the line, handed to the freeze trigger, and used to pick a package basis.
// It must be the SAME source rowCountUnitOf uses below, or the line claims a
// unit its own need was never computed in: a PAR typed as "1 case" made
// par_unit win here and wrote count_unit = 'case' onto a need that had been
// worked out in each, and the trigger then chose a package basis for the
// wrong dimension. par_unit is deliberately absent for the reason spelled out
// at rowCountUnitOf — a PAR's unit is a claim about the PAR, not about the
// shelf.
const resolveCountUnit = (ci) =>
  ci?.item_snapshot?.unit ?? ci?.count_unit ?? ci?.item_snapshot?.count_unit ?? null;

// Item replenishment rules (inventory_items.replenishment_*_qty, in the COUNT
// unit) ride on the need so buildAllocationLine can shape the purchase target
// exactly as the server trigger will. Read from the row or its snapshot —
// whichever the caller's fetch carried.
//
// GROUP OWNER RULE (Part 16 of the ordering correction): for a folded
// Equivalent group the need line is keyed to the group's stable basis member,
// and after the fold only THAT member's rows remain under its id — so the
// replenishment policy that applies is the basis member's own, never a
// max/min across siblings, and it cannot change merely because another
// sibling becomes favored (the basis is sticky). The server trigger and
// updateLineOption read inventory_items by the same line id, so all three
// layers name the same owner. These are ITEM stocking policies in the count
// unit — never a vendor MOQ.
const resolveReplenishment = (ci, field) =>
  toFiniteNumber(ci?.[field]) ?? toFiniteNumber(ci?.item_snapshot?.[field]);

// Mirrors computeSharedOrderData: a row contributes to on-hand only once it
// has actually been counted.
const isCountedRow = (ci) =>
  !!ci?.is_counted || (ci?.current_count !== '' && ci?.current_count != null);

// The row's own count unit — what resolveRowPar compares par_unit against,
// the same source order the group fold and countPresentation use. NO
// par_unit fallback: a PAR's own unit compared against itself passes every
// gate, which is how "2 cases" on a unit-less item became the number 2.
const rowCountUnitOf = (ci) => ci?.item_snapshot?.unit ?? ci?.count_unit ?? '';

/**
 * Aggregates raw count-item rows (possibly several location rows per item)
 * into one need per inventory item:
 *
 *   par    — max NORMALIZED PAR across the item's rows (resolveRowPar: stored
 *            par_normalized_quantity wins; a legacy par_level counts only in
 *            the row's own count unit; an ambiguous "2 cases" resolves to
 *            nothing and is reported as unresolved, never used as 2)
 *   onHand — sum of current_count across counted rows
 *
 * Only items with NEED = computeNeed(par, onHand) > 0 are returned, in the
 * shape buildAllocationLine expects:
 * [{ inventory_item_id, description, par, onHand, count_unit }]
 *
 * A confirmed Equivalent group is ONE operational need. When `opts.groups` is
 * provided (buildWorkingSetGroups output) and the rows have not already been
 * folded by the count screen, the same planCountGroupFold used at count time
 * runs here first — so one group yields one need line regardless of whether a
 * display fold happened to run upstream. Already-folded rows pass through
 * unchanged (their hidden siblings are absent, so re-folding is a no-op and
 * the stamps they carry are read exactly as before).
 *
 * PREPARED items are excluded. A prepared item's shortage is real, but it is
 * answered by making another batch, not by buying one — there is no vendor to
 * send the line to, and the only way to put House Pizza Sauce on a purchase
 * order is to invent a supplier for it. Those shortages go to the prep list
 * instead (`utils/preparedReplenishment.buildPrepList`), which is the whole
 * point of the branch. The exception is a prepared item that also has a real,
 * approved purchase option — a store that makes its own sauce and buys it when
 * they run short — which is a genuine vendor line and stays here.
 *
 * @param {Object} [opts]
 * @param {Object} [opts.optionsByItemId] inventory_item_id → purchase_options[],
 *   used only to spot a prepared item's legitimate purchased alternative.
 * @param {Object} [opts.groups] buildWorkingSetGroups output; makes the need
 *   builder group-aware independent of the display fold.
 * @param {Map}    [opts.stickyCanonicalByGroupId] group id → sticky canonical
 *   member (equivalent_group_comparisons.canonical_item_id), the group's
 *   stock-basis anchor when re-folding here.
 * @param {Array}  [opts.unresolved] when provided, rows whose STATED PAR could
 *   not be normalized are appended as { inventory_item_id, description,
 *   par_display } so callers can surface them instead of silently ordering
 *   nothing (an unresolved PAR is a question, not a zero).
 */
export function buildNeedsFromCountItems(countItems = [], opts = {}) {
  const optionsByItemId = opts?.optionsByItemId || {};
  let rows = (countItems || []).filter(Boolean);

  // Fold-independence: one Equivalent group = one purchasing need, whether or
  // not the count screen's display fold ran. planCountGroupFold is the ONE
  // fold implementation — reused, not reimplemented.
  if (opts?.groups?.membersByGroupId?.size) {
    try {
      const presentIds = rows.map((ci) => resolveInvId(ci)).filter((id) => id != null);
      const restricted = restrictGroupsToItems(opts.groups, presentIds);
      if (restricted.membersByGroupId.size) {
        rows = planCountGroupFold({
          rows,
          groups: restricted,
          stickyCanonicalByGroupId: opts.stickyCanonicalByGroupId,
        }).visibleRows;
      }
    } catch (foldErr) {
      // Non-fatal by the same contract as the count screen: a fold failure
      // degrades to item-scoped needs, never to a failed plan.
      console.warn('[allocation] group fold unavailable (non-fatal):', foldErr?.message);
    }
  }

  const groups = new Map();
  rows.forEach((ci) => {
    const invId = resolveInvId(ci);
    if (invId === null || invId === undefined) return;
    if (isPreparedRow(ci) && !hasPurchasedAlternative({ ...ci, inventory_item_id: invId }, optionsByItemId)) {
      return;
    }
    if (!groups.has(invId)) {
      groups.set(invId, {
        inventory_item_id: invId,
        description: null,
        par: 0,
        onHand: 0,
        count_unit: null,
        replenishment_minimum_qty: null,
        replenishment_increment_qty: null,
      });
    }
    const group = groups.get(invId);
    if (!group.description) group.description = resolveDescription(ci);
    if (!group.count_unit) group.count_unit = resolveCountUnit(ci);
    if (group.replenishment_minimum_qty == null) {
      group.replenishment_minimum_qty = resolveReplenishment(ci, 'replenishment_minimum_qty');
    }
    if (group.replenishment_increment_qty == null) {
      group.replenishment_increment_qty = resolveReplenishment(ci, 'replenishment_increment_qty');
    }

    const resolution = resolveRowPar(ci, rowCountUnitOf(ci));
    if (resolution.status === PAR_STATUS.VALID && resolution.normalized !== null) {
      if (resolution.normalized > group.par) group.par = resolution.normalized;
    } else if (resolution.display?.value != null && Array.isArray(opts?.unresolved)) {
      // A stated PAR the normalized resolution refused ("2 cases", no
      // package). It must not become the number 2 — and it must not silently
      // become "no need" either, so the caller gets to say so.
      opts.unresolved.push({
        inventory_item_id: invId,
        description: resolveDescription(ci),
        par_display: resolution.display,
      });
    }
    if (isCountedRow(ci)) group.onHand += toFiniteNumber(ci?.current_count) || 0;
    // The related-group fold (utils/countGroupFold) hides sibling members'
    // rows and stamps their contribution onto the favored member's first
    // row: hidden typed counts join on-hand, the max hidden normalized PAR
    // joins the PAR. One stamped row per group, so this never double-counts.
    // This is what makes 3 lime items with PAR 4 each and 12 counted need 0,
    // not 8.
    const baseline = toFiniteNumber(ci?.group_baseline_count);
    if (baseline !== null) group.onHand += baseline;
    const parFloor = toFiniteNumber(ci?.group_par_floor);
    if (parFloor !== null && parFloor > group.par) group.par = parFloor;
    if (ci?.group_par_unresolved && Array.isArray(opts?.unresolved)) {
      opts.unresolved.push({
        inventory_item_id: invId,
        description: resolveDescription(ci),
        par_display: null,
      });
    }
    // Members of this Equivalent group state different stocking levels. The
    // need still uses the group's resolved PAR so the line stays buyable —
    // that is the whole point of not blocking on it — but the disagreement
    // rides along so the review can ask which policy to keep rather than
    // letting the larger number win in silence forever.
    if (ci?.group_par_conflict && !group.par_conflict) {
      group.par_conflict = ci.group_par_conflict;
    }
  });

  return [...groups.values()].filter((group) => computeNeed(group.par, group.onHand) > 0);
}

/**
 * Creates a draft purchase_allocations row plus one line per needed item.
 * Each line is priced by buildAllocationLine against the item's allocatable
 * options (optionsByItemId keyed by inventory_item_id, e.g. from
 * purchaseOptionService.fetchOptionsForItems).
 *
 * @returns {Promise<{ allocation: Object, lines: Array }>}
 */
export async function createDraftAllocation(
  { storeId, countSessionId, countItems = [], optionsByItemId = {} } = {},
  client = sharedSupabase,
) {
  if (!storeId) throw new Error('storeId is required');

  const { data: allocation, error: allocationError } = await client
    .from('purchase_allocations')
    .insert({
      store_id: storeId,
      count_session_id: countSessionId ?? null,
      status: 'draft',
    })
    .select()
    .single();
  throwIfError(allocationError);

  // The options map is passed through so a prepared item with a genuine
  // purchase option still allocates, while a prepared-only one never does.
  const needs = buildNeedsFromCountItems(countItems, { optionsByItemId });
  if (!needs.length) return { allocation, lines: [] };

  const lineRows = needs.map((item) => {
    const options = optionsByItemId?.[item.inventory_item_id] || [];
    return {
      allocation_id: allocation.id,
      store_id: storeId,
      ...forStorageWithRecommendedBaseline(buildAllocationLine({ item, options })),
    };
  });

  let { data: lines, error: linesError } = await client
    .from('purchase_allocation_lines')
    .insert(lineRows)
    .select();
  if (linesError && isMissingColumnError(linesError) && lineRows.some((row) => 'selection_reason' in row)) {
    const stripped = lineRows.map(({
      selection_reason: _reason,
      recommended_purchase_option_id: _recommendedOption,
      recommended_selection_reason: _recommendedReason,
      ...row
    }) => row);
    ({ data: lines, error: linesError } = await client
      .from('purchase_allocation_lines')
      .insert(stripped)
      .select());
  }
  throwIfError(linesError);

  return { allocation, lines: lines || [] };
}

/**
 * Re-prices a draft line after the user picks a different purchase option and
 * persists the recalculated package/cost math. `option` is the full
 * purchase_options row the user selected (null clears the selection).
 *
 * @returns {Promise<Object>} the updated purchase_allocation_lines row
 */
export async function updateLineOption(lineId, optionId, option, client = sharedSupabase) {
  if (!lineId) throw new Error('lineId is required');

  // count_unit must ride along so recalculateLine keeps the need-unit gate ON
  // during vendor switches — without it a switch prices the package in the
  // wrong unit family unchecked.
  const { data: line, error: fetchError } = await client
    .from('purchase_allocation_lines')
    .select('id, quantity_needed, count_unit, inventory_item_id')
    .eq('id', lineId)
    .single();
  throwIfError(fetchError);

  // The item's replenishment rules shape the purchase target exactly as the
  // confirm trigger will; repricing without them would store a packages count
  // the fan-out gate then rejects ("Package changed; review again"). Missing
  // columns (store behind on migrations) degrade to no item stage.
  let itemRules = null;
  if (line?.inventory_item_id != null) {
    const { data: itemRow, error: itemError } = await client
      .from('inventory_items')
      .select('id, unit, replenishment_minimum_qty, replenishment_increment_qty')
      .eq('id', line.inventory_item_id)
      .maybeSingle();
    if (!itemError && itemRow) itemRules = itemRow;
  }

  // The item's OWN options, for the count unit's physical definition. A
  // manual vendor switch has to be computed by the same rules the automatic
  // selection used — buildAllocationLine derives this bridge, and a switch
  // that skipped it turned a resolvable container-unit need into "package
  // needs review" purely because a person chose the vendor by hand.
  let itemOptions = null;
  if (line?.inventory_item_id != null) {
    const { data: optionRows, error: optionsError } = await client
      .from('purchase_options')
      .select('*')
      .eq('inventory_item_id', line.inventory_item_id)
      .eq('status', 'active');
    if (!optionsError && Array.isArray(optionRows)) itemOptions = optionRows;
  }

  const next = recalculateLine(
    { ...line, ...(itemRules || {}) },
    option || null,
    {
      itemOptions,
      item: itemRules
        ? { id: itemRules.id ?? line.inventory_item_id, unit: itemRules.unit ?? line.count_unit ?? null }
        : null,
    },
  );
  const patch = {
    selected_purchase_option_id: optionId ?? null,
    // A person picked this vendor (or cleared the pick) — record why the line
    // points where it points, so the frozen snapshot can say so later.
    selection_reason: optionId ? 'explicit' : null,
    packages_required: next.packages_required,
    quantity_received: next.quantity_received,
    overage: next.overage,
    unit_cost: next.unit_cost,
    extended_cost: next.extended_cost,
    warnings: next.warnings,
  };
  // A line already fanned out into a PO is frozen — a stale worksheet tab
  // must not reprice or re-point it. Zero rows updated = the vendor finished
  // while this tab was open.
  let { data, error } = await client
    .from('purchase_allocation_lines')
    .update(patch)
    .eq('id', lineId)
    .is('fanned_out_at', null)
    .select()
    .single();
  if (error && isMissingColumnError(error)) {
    const { selection_reason: _reason, ...stripped } = patch;
    ({ data, error } = await client
      .from('purchase_allocation_lines')
      .update(stripped)
      .eq('id', lineId)
      .select()
      .single());
  }
  throwIfError(error);
  return data;
}

// Maps confirm_purchase_allocation RPC failures to messages a buyer can act on.
const friendlyConfirmError = (message) => {
  const text = String(message || '');
  if (text.includes('not found')) {
    return 'This purchase allocation no longer exists. It may have been deleted.';
  }
  if (text.includes('does not have access')) {
    return 'You do not have access to the store this allocation belongs to.';
  }
  if (text.includes('only draft allocations can be confirmed')) {
    return 'This allocation has already been confirmed or canceled.';
  }
  if (text.includes('is not active')) {
    return 'A selected purchase option is no longer active. Pick another option and try again.';
  }
  if (text.includes('belongs to store')) {
    return 'A selected purchase option belongs to a different store. Rebuild the allocation.';
  }
  if (text.includes('is for inventory item')) {
    return 'A selected purchase option does not match its allocation line. Rebuild the allocation.';
  }
  if (text.includes('does not exist')) {
    return 'A selected purchase option no longer exists. Pick another option and try again.';
  }
  return text || 'Failed to confirm the purchase allocation.';
};

/**
 * Confirms a draft allocation via the confirm_purchase_allocation RPC, which
 * freezes option snapshots on the lines and fans out per vendor: delivery →
 * draft purchase_orders, pickup → shopping_lists, no vendor → unassigned.
 * Idempotent — re-confirming returns the originally created ids.
 *
 * @returns {Promise<{ purchase_order_ids: Array, shopping_list_ids: Array, unassigned_inventory_item_ids: Array }>}
 */
export async function confirmAllocation(allocationId, client = sharedSupabase) {
  if (!allocationId) throw new Error('allocationId is required');
  const { data, error } = await client.rpc('confirm_purchase_allocation', {
    p_allocation_id: allocationId,
  });
  if (error) throw new Error(friendlyConfirmError(error.message));

  const parsed = typeof data === 'string' ? JSON.parse(data) : data || {};
  return {
    purchase_order_ids: parsed.purchase_order_ids || [],
    shopping_list_ids: parsed.shopping_list_ids || [],
    unassigned_inventory_item_ids: parsed.unassigned_inventory_item_ids || [],
  };
}

/**
 * Latest allocation for a count session with its lines, each line embedding
 * the selected purchase option (as `option`) for the review UI.
 *
 * @returns {Promise<Object|null>} allocation row with `lines`, or null when
 *                                  the session has no allocation yet.
 */
export async function fetchAllocationForSession(storeId, countSessionId, client = sharedSupabase) {
  if (!storeId || !countSessionId) return null;

  const { data: allocation, error } = await client
    .from('purchase_allocations')
    .select('*')
    .eq('store_id', storeId)
    .eq('count_session_id', countSessionId)
    .order('created_at', { ascending: false })
    .limit(1)
    .maybeSingle();
  throwIfError(error);
  if (!allocation) return null;

  const { data: lines, error: linesError } = await client
    .from('purchase_allocation_lines')
    // The embed names its foreign key on purpose. purchase_allocation_lines
    // has had TWO paths to purchase_options since
    // 20260824120000_purchase_plan_recommended_baseline added
    // recommended_purchase_option_id beside selected_purchase_option_id, and an
    // unqualified purchase_options(...) is answered with 300/PGRST201 rather
    // than a guess. `option` is the line's CURRENT choice, so it is the
    // selected-option constraint; the recommendation is a separate baseline
    // that display never resolves through this join.
    .select('*, option:purchase_options!purchase_allocation_lines_selected_purchase_option_id_fkey(*)')
    .eq('allocation_id', allocation.id)
    .order('created_at', { ascending: true });
  throwIfError(linesError);

  return { ...allocation, lines: lines || [] };
}
